'use client';

/**
 * Bodega (y ubicación) de la línea del pedido.
 *
 * Antes la bodega era un input de solo lectura: la traía el ítem del catálogo
 * de SIESA y no había forma de cambiarla, así que un pedido que debía salir de
 * otra bodega tocaba corregirlo después en el ERP. Aquí se busca contra el
 * maestro real (`get-warehouses`) y se puede cambiar por línea.
 *
 * Cuando la bodega escogida es multi-ubicación se muestra además el selector de
 * ubicación, con las ubicaciones reales de esa bodega
 * (`get-location-by-warehouse`). Va como dato del CRM: el plano de PEDIDO de
 * SIESA (t431) no tiene columna de ubicación, así que no viaja al ERP.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Select, Space, Tag, Typography } from 'antd';
import type { FormInstance } from 'antd/es/form';
import {
  SiesaWarehouse,
  SiesaWarehouseLocation,
  getWarehouseLocations,
  getWarehouses,
} from '@/services/OrderAssemblyService';

const { Text } = Typography;

/** Maestro de bodegas, compartido por todos los selectores de la pantalla. */
export const useWarehouseCatalog = () => {
  const [warehouses, setWarehouses] = useState<SiesaWarehouse[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getWarehouses()
      .then((list) => {
        if (alive) setWarehouses(list);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const byCode = useMemo(() => {
    const map = new Map<string, SiesaWarehouse>();
    warehouses.forEach((w) => map.set(w.warehouse.toUpperCase(), w));
    return map;
  }, [warehouses]);

  return { warehouses, byCode, loading };
};

/** ¿Esta bodega pide ubicación? (multi-ubicación en el maestro de SIESA) */
export const warehouseNeedsLocation = (
  code: string | undefined | null,
  byCode: Map<string, SiesaWarehouse>,
): boolean => {
  if (!code) return false;
  return !!byCode.get(String(code).trim().toUpperCase())?.multi_location;
};

const warehouseOptionLabel = (w: SiesaWarehouse) => (
  <Space size={6} style={{ width: '100%', justifyContent: 'space-between' }}>
    <span>
      <Text strong style={{ fontSize: 13 }}>{w.warehouse}</Text>
      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>{w.warehouse_name}</Text>
    </span>
    {w.multi_location ? (
      <Tag color="blue" bordered={false} style={{ marginInlineEnd: 0, fontSize: 11 }}>
        ubicación
      </Tag>
    ) : null}
  </Space>
);

export interface WarehouseSelectProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  warehouses: SiesaWarehouse[];
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  size?: 'small' | 'middle' | 'large';
}

/** Selector de bodega, buscable por código y por nombre. */
export const WarehouseSelect: React.FC<WarehouseSelectProps> = ({
  value,
  onChange,
  warehouses,
  loading,
  disabled,
  placeholder = 'Busca la bodega por código o nombre',
  size,
}) => {
  const options = useMemo(
    () =>
      warehouses.map((w) => ({
        value: w.warehouse,
        // `label` es lo que queda escrito en el input al elegir; el desplegable
        // usa el nodo rico de abajo.
        label: `${w.warehouse} · ${w.warehouse_name}`,
        title: w.warehouse_name,
        multi: w.multi_location,
        node: warehouseOptionLabel(w),
      })),
    [warehouses],
  );

  // La bodega que trae el ítem puede no estar en el maestro (bodega inactiva,
  // o el ERP no respondió). Se agrega como opción suelta para no borrarla.
  const known = options.some((o) => o.value === value);
  const allOptions = !known && value
    ? [{ value, label: value, title: value, multi: false, node: <Text>{value}</Text> }, ...options]
    : options;

  return (
    <Select
      showSearch
      allowClear
      size={size}
      value={value || undefined}
      onChange={(v) => onChange?.(v ? String(v) : undefined)}
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      style={{ width: '100%' }}
      optionLabelProp="label"
      filterOption={(input, option) => {
        const needle = input.trim().toLowerCase();
        if (!needle) return true;
        return String((option as any)?.label || '').toLowerCase().includes(needle);
      }}
      options={allOptions.map((o) => ({ value: o.value, label: o.label, title: o.title }))}
      optionRender={(option) => {
        const found = allOptions.find((o) => o.value === option.value);
        return found ? found.node : option.label;
      }}
      notFoundContent={loading ? 'Consultando bodegas…' : 'Sin bodegas que coincidan'}
    />
  );
};

export interface LocationSelectProps {
  warehouse?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
}

/** Selector de ubicación de una bodega multi-ubicación. */
export const LocationSelect: React.FC<LocationSelectProps> = ({
  warehouse,
  value,
  onChange,
  disabled,
  size,
}) => {
  const [locations, setLocations] = useState<SiesaWarehouseLocation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    const code = String(warehouse || '').trim();
    if (!code) {
      setLocations([]);
      return () => {
        alive = false;
      };
    }
    setLoading(true);
    getWarehouseLocations(code)
      .then((list) => {
        if (alive) setLocations(list);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [warehouse]);

  const options = useMemo(
    () =>
      locations.map((l) => ({
        value: l.name,
        label: l.description && l.description !== l.name ? `${l.name} · ${l.description}` : l.name,
      })),
    [locations],
  );

  return (
    <Select
      showSearch
      allowClear
      size={size}
      value={value || undefined}
      onChange={(v) => onChange?.(v ? String(v) : undefined)}
      loading={loading}
      disabled={disabled || !warehouse}
      placeholder={warehouse ? 'Ubicación dentro de la bodega' : 'Escoge primero la bodega'}
      style={{ width: '100%' }}
      options={options}
      filterOption={(input, option) =>
        String(option?.label || '').toLowerCase().includes(input.trim().toLowerCase())
      }
      notFoundContent={loading ? 'Consultando ubicaciones…' : 'Esta bodega no tiene ubicaciones'}
    />
  );
};

export interface WarehouseFieldsProps {
  form: FormInstance;
  /** Nombre del campo de bodega en el formulario. */
  warehouseName?: string;
  /** Nombre del campo de ubicación en el formulario. */
  locationName?: string;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  required?: boolean;
  /** Catálogo compartido; si no se pasa, el componente lo carga por su cuenta. */
  catalog?: ReturnType<typeof useWarehouseCatalog>;
}

/**
 * Los dos campos juntos, con la regla de negocio: al cambiar de bodega se
 * limpia la ubicación (una ubicación pertenece a UNA bodega) y el selector de
 * ubicación solo aparece si la bodega nueva es multi-ubicación.
 */
export const WarehouseFields: React.FC<WarehouseFieldsProps> = ({
  form,
  warehouseName = 'warehouse',
  locationName = 'location',
  disabled,
  size,
  required = true,
  catalog,
}) => {
  const ownCatalog = useWarehouseCatalog();
  const { warehouses, byCode, loading } = catalog ?? ownCatalog;
  const useWatch: any = (Form as any).useWatch;
  const warehouse: string | undefined = useWatch(warehouseName, form);
  const needsLocation = warehouseNeedsLocation(warehouse, byCode);

  const handleWarehouseChange = useCallback(
    (next: string | undefined) => {
      const values: Record<string, any> = { [warehouseName]: next };
      if (!warehouseNeedsLocation(next, byCode)) values[locationName] = undefined;
      form.setFieldsValue(values);
    },
    [byCode, form, locationName, warehouseName],
  );

  return (
    <>
      <Form.Item
        name={warehouseName}
        label="Bodega"
        rules={required ? [{ required: true, message: 'Seleccione bodega' }] : undefined}
      >
        <WarehouseSelect
          warehouses={warehouses}
          loading={loading}
          disabled={disabled}
          size={size}
          onChange={handleWarehouseChange}
        />
      </Form.Item>

      {needsLocation ? (
        <Form.Item
          name={locationName}
          label="Ubicación"
          // Opcional a propósito: SIESA no lleva ubicación en el pedido de venta
          // (la pide en los movimientos de inventario). Se guarda en el CRM para
          // saber de dónde sale la mercancía.
          extra={
            <Text type="secondary" style={{ fontSize: 11 }}>
              Bodega multi-ubicación · opcional, queda registrada en el CRM
            </Text>
          }
        >
          <LocationSelect warehouse={warehouse} disabled={disabled} size={size} />
        </Form.Item>
      ) : null}
    </>
  );
};

export default WarehouseFields;
